import _ from 'lodash';
import router from '@/router';
import Vue from 'vue';
import endpoints from '@/constants/endpoints';
import http from '@/utils/http';
import responseHelper from '@/utils/response-helper';
import sessionStorageHelper from '@/utils/sessionStorage-helper';

import {
  AUTH_REQUEST,
  AUTH_SUCCESS,
  AUTH_LOGOUT,
  AUTH_TWOFA_VERIFY,
  AUTH_TWOFA_CONFIRM,
  AUTH_UPDATE_2FA_FLAG,
  AUTH_TWOFA_UPDATE,
  AUTH_CLEAR_TWO_FA,
  AUTH_CONFIRM_IP,
  AUTH_NAME_UPDATE,
  AUTH_GET_USER_PERMISSIONS,
  AUTH_UPDATE_USER_PERMISSIONS,
} from '@/store/actions/auth';

const state = {
  status: '',
  hasLoadedOnce: false,
  userId: '',
  userEmail: '',
  userName: '',
  twofaSecret: '',
  twofaEnableFlag: false,
  createdAt: '',
  userStatus: '',
  role: null,
  twofaVerify: {},
  isComfirmedIp: !false,
  userPermissions: [],
  userPartnerType: '',
  userCountryCode: '',
};

const getters = {
  isAuthenticated: state => !!state.userId,
  role: state => state.role,
  authStatus: state => state.status,
  userId: state => state.userId,
  userEmail: state => state.userEmail,
  userName: state => state.userName,
  twofaSecret: state => state.twofaSecret,
  twofaEnableFlag: state => state.twofaEnableFlag,
  createdAt: state => state.createdAt,
  userStatus: state => state.userStatus,
  twofaVerify: state => state.twofaVerify,
  userPermissions: state => state.userPermissions,
  userPartnerType: state => state.userPartnerType,
  userCountryCode: state => state.userCountryCode,
};

const actions = {
  clear2Fa(context) {
    context.commit(AUTH_CLEAR_TWO_FA);
  },
  async updateProfileName({ commit }, val) {
    try {
      const data = {
        name: val.name,
      };
      const res = await http.put(endpoints.updateProfileName, data);
      let names = null;
      if (res.data) {
        names = res.data.name;
      }
      commit(AUTH_NAME_UPDATE, names);
      Vue.notify({
        group: 'notifications',
        type: 'success',
        title: responseHelper.getMessageByCode('AUTH_PROFILE_PAGE_NOTIFY_SUCCESS_TITLE'),
        text: responseHelper.getMessageByCode('AUTH_PROFILE_PAGE_NOTIFY_SUCCESS_MESSAGE'),
      });
    }
    catch (err) {
      Vue.notify({
        group: 'notifications',
        type: 'error',
        title: responseHelper.getMessageByCode('AUTH_PROFILE_PAGE_NOTIFY_ERROR_TITLE'),
        text: responseHelper.getErrorMessage(err),
      });
    }
  },

  [AUTH_REQUEST]: (context, data) => {
    return new Promise((resolve, reject) => {
      context.dispatch('clear2Fa').then(() => {
        const params = {
          email: data.email,
          password: data.password,
          'g-recaptcha-response': data['g-recaptcha-response']
        };

        http.post(endpoints.login, params)
          .then(response => {
            if (response.data.confirm_ip) {
              context.commit(AUTH_CONFIRM_IP, response.data);
            } else if (response.data.twofa) {
              context.commit(AUTH_TWOFA_VERIFY, response.data);
            } else {
              setTimeout(() => {
                context.dispatch(AUTH_GET_USER_PERMISSIONS, data);
              }, 10);

              context.commit(AUTH_SUCCESS, {
                user: response.data.user,
                partner: response.data.partner,
                redirect: data.redirect
              });
            }

            resolve();
          })
          .catch(err => {
            Vue.notify({
              group: 'notifications',
              type: 'error',
              title: responseHelper.getMessageByCode('AUTH_PROFILE_PAGE_NOTIFY_ERROR_TITLE'),
              text: responseHelper.getErrorMessage(err),
              duration: 5 * 1000,
            });
          });
      });
    });
  },

  [AUTH_TWOFA_CONFIRM]: (context, { code, redirectUrl }) => {
    const data = {
      verify_token: context.state.twofaVerify.verify_token,
      twofa_code: code
    };

    http
      .post(endpoints.confirm2Fa, data)
      .then(response => {
        setTimeout(() => {
          context.dispatch(AUTH_GET_USER_PERMISSIONS, { redirect: redirectUrl });
        }, 10);

        context.commit(AUTH_SUCCESS, {
          user: response.data.user,
          redirect: redirectUrl,
        });
      })
      .catch(err => {
        Vue.notify({
          group: 'notifications',
          type: 'error',
          title: 'Error',
          text: responseHelper.getErrorMessage(err),
        });
      });
  },

  [AUTH_TWOFA_UPDATE]: (context, params) => {
    return new Promise((resolve, reject) => {

      http
        .post(endpoints.update2Fa, params)
        .then(response => {
          console.log(response);
          context.commit(AUTH_UPDATE_2FA_FLAG, {
            flag: !params.disable
          });
          resolve();
        })
        .catch(err => {
          Vue.notify({
            group: 'notifications',
            type: 'error',
            title: 'Error',
            text: responseHelper.getErrorMessage(err),
          });
        });
    });
  },

  [AUTH_LOGOUT]: (context, dontRedirectToLoginPage) => {
    http
      .get(endpoints.logout)
      .then(ressponse => {
        context.commit(AUTH_LOGOUT, dontRedirectToLoginPage);
      })
      .catch(err => {
        Vue.notify({
          group: 'notifications',
          type: 'error',
          title: 'Error',
          text: responseHelper.getErrorMessage(err),
        });
      });
  },

  [AUTH_GET_USER_PERMISSIONS]: async (context, authRequestData) => {
    const isAuthenticated = context.getters.isAuthenticated;
    if (!isAuthenticated) {
      return;
    }

    try {
      const params = {
        offset: 0,
        limit: Number.MAX_SAFE_INTEGER,
      };
      const result = await http.get(endpoints.getUserPermissions, { params });
      const items = result.data ? result.data.items : [];
      const data = {
        items,
        redirect: authRequestData ? authRequestData.redirect : '',
      };

      context.commit(AUTH_UPDATE_USER_PERMISSIONS, data);
    }
    catch (err) {
      Vue.notify({
        group: 'notifications',
        type: 'error',
        title: 'Error',
        text: responseHelper.getErrorMessage(err),
      });
    }
  },

};

const mutations = {
  [AUTH_TWOFA_VERIFY]: (state, args) => {
    state.twofaVerify = { ...args };
  },

  [AUTH_NAME_UPDATE]: (state, userName) => {
    state.userName = userName;
  },

  [AUTH_CLEAR_TWO_FA]: state => {
    state.twofaVerify = {};
  },

  [AUTH_CONFIRM_IP]: (state, args) => {
    state.isComfirmedIp = true;
  },

  [AUTH_SUCCESS]: (state, { user, partner, redirect }) => {
    state;
    state.status = 'success';
    state.userId = user.id;
    state.userEmail = user.email;
    state.userName = user.name;
    state.twofaEnableFlag = user.twofa_enable_flg;
    state.role = user.roles[0];
    state.createdAt = user.create_at;
    state.userStatus = user.user_sts;
    state.hasLoadedOnce = true;
    state.isComfirmedIp = false;
    state.userPartnerType = partner.partner_type;
    state.userCountryCode = user.country_code;
  },

  [AUTH_LOGOUT]: (state, dontRedirectToLoginPage) => {
    state.status = '';
    state.hasLoadedOnce = false;
    state.userId = '';
    state.userEmail = '';
    state.userName = '';
    state.twofaSecret = '';
    state.twofaEnableFlag = false;
    state.role = null;
    state.createdAt = '';
    state.userStatus = '';
    state.twofaVerify = {};
    state.isConfirmIp = false;
    state.userPermissions = [];
    state.userPartnerType = null;
    state.userCountryCode = '',
    sessionStorageHelper.clearData();

    if (!dontRedirectToLoginPage) {
      router.push('/login');
    }
  },

  [AUTH_UPDATE_2FA_FLAG]: (state, { flag }) => {
    state.twofaEnableFlag = flag;
  },

  [AUTH_UPDATE_USER_PERMISSIONS]: (state, { items, redirect }) => {
    // Master
    if (state.role && state.role.root_flg) {
      items.push({ name: 'ROOT' });
    }

    const userPartnerType = state.userPartnerType;
    // https://iblb2b.atlassian.net/browse/STAK-1320
    // [ChildPool] Change menu base on Type of child pool
    if (userPartnerType == 'CHILD') {
      // items.push({ name: 'MEMBERSHIP_VIEW_MEMBER_LIST' });
      _.remove(items, item => item.name === 'VIEW_LIST_CHILDPOOL_GRANDCHILD');
    } else if (userPartnerType == 'AFFILIATE') {
      // items.push({ name: 'VIEW_LIST_CHILDPOOL_GRANDCHILD' });
      _.remove(items, item => item.name === 'MEMBERSHIP_VIEW_MEMBER_LIST');
    }

    state.userPermissions = items;

    if (redirect) {
      router.push(redirect);
    }
  },

};

export default {
  state,
  getters,
  actions,
  mutations
};
